import { Facebook, Instagram, MapPin, Phone } from "lucide-react";
import logo from "@/assets/oz-move-logo.webp";

/**
 * Simple TikTok brand icon (lucide-react does not ship brand icons).
 * Stroke weight and viewBox are tuned to match the surrounding Lucide icons.
 */
const TikTokIcon = ({ className, strokeWidth = 1.8 }: { className?: string; strokeWidth?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4c.5 1.5 2 3 4 3" />
  </svg>
);

/**
 * Official OZ MOVE CO. social profile URLs.
 * Edit these here to update the footer social icons.
 */
const socialLinks = [
  { label: "Visit OZ MOVE CO. on Facebook", href: "https://www.facebook.com/profile.php?id=61588732268747&sk=directory_contact_info", Icon: Facebook },
  { label: "Visit OZ MOVE CO. on Instagram", href: "https://www.instagram.com/ozmove.co/", Icon: Instagram },
  { label: "Visit OZ MOVE CO. on TikTok", href: "https://www.tiktok.com/@oz.move.co", Icon: TikTokIcon },
  { label: "View OZ MOVE CO. on Google Maps", href: "https://maps.app.goo.gl/cmVjAMDB96gwQHwV9", Icon: MapPin },
];

const footerLinks = {
  Services: [
    { label: "Home Moves", href: "/home-moves" },
    { label: "Apartment Moves", href: "/apartment-moves" },
    { label: "Office Relocations", href: "/office-relocations" },
    { label: "Single Item Moves", href: "/single-item-moves" },
    { label: "Same Day Moves", href: "/same-day-moves" },
  ],
  Locations: [
    { label: "Melbourne", href: "/melbourne" },
    { label: "Sydney", href: "/sydney" },
    { label: "Brisbane", href: "/brisbane" },
    { label: "Perth", href: "/perth" },
    { label: "Adelaide", href: "/adelaide" },
    { label: "Gold Coast", href: "/gold-coast" },
    { label: "All Locations", href: "/locations" },
  ],
  Company: [
    { label: "How It Works", href: "/#how-it-works" },
    { label: "Reviews", href: "/#reviews" },
    { label: "Get a Quote", href: "/#quote" },
    { label: "Why Choose Us", href: "/#why-choose-us" },
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Blog", href: "/blog" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-conditions" },
  ],
};

const Footer = () => (
  <footer className="bg-[#111111] pt-14 pb-8">
    <div className="container">
      {/* Top row - logo, tagline, phone */}
      <div className="mb-10">
        <img src={logo} alt="OZ MOVE CO." className="h-28 md:h-32 w-auto mb-3" width="384" height="256" />
        <p className="text-[#FFC300] font-bold text-base">Fast. Reliable. Stress Free.</p>
        <p className="text-white/60 text-xs mt-1.5">Fully Insured. 5 Star Service</p>

        <div className="mt-5">
          <p className="text-white/65 text-[11px] uppercase tracking-wider font-semibold mb-1.5">Call us</p>
          <a
            href="tel:1800556654"
          data-call-source="footer"
            className="flex items-center gap-2 text-[#FFC300] font-bold text-xl hover:opacity-80 transition-opacity w-fit"
          >
            <Phone className="w-5 h-5" />
            1800 556 654
          </a>
        </div>
      </div>

      {/* Link columns */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-10">
        {Object.entries(footerLinks).map(([heading, links]) => (
          <div key={heading}>
            <p className="font-bold text-white text-sm mb-3.5">{heading}</p>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/45 text-sm hover:text-white/70 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Social icons */}
      <div className="mb-8 flex justify-center md:justify-start gap-3">
        {socialLinks.map(({ label, href, Icon }, index) => (
          <a
            key={label}
            href={href}
            aria-label={label}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-white/50 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FFC300]/50 hover:text-[#FFC300] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC300]/60 ${
              index === socialLinks.length - 1 ? "ml-2 md:ml-3" : ""
            }`}
          >
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </a>
        ))}
      </div>

      {/* Divider + copyright + business details */}
      <div className="border-t border-white/10" />
      <div className="pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-white/60 text-xs text-center md:text-left">
        <p>© 2026 OZ MOVE CO. Pty Ltd. All rights reserved.</p>
        <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-4 md:justify-end">
          <span>Melbourne, Victoria</span>
          <a
            href="tel:1800556654"
            data-call-source="footer_business_details"
            aria-label="Call 1800 556 654"
            className="hover:text-[#FFC300] transition-colors"
          >
            1800 556 654
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
